import type { ManifestEntry } from './updateCheck';

// Fraction of the bundle above which the resumable zip beats the manifest; 0.5 assumes Brotli delta objects.
// `.br` names miss the iOS builtin-bundle lookup, so a fresh iOS install probes near 1.0 and takes the
// zip by design, not as a regression.
export const DELTA_MAX_RATIO = 0.5;

export interface DeltaProbeResult {
  filesTotal: number;
  filesMissing: number;
  probeMs: number;
  // Sum of `size` over the whole manifest / the missing set.
  bytesTotal: number | null;
  bytesMissing: number | null;
}

interface ProbeDeps {
  getMissingBundleFiles: (options: { manifest: ManifestEntry[]; version: string }) => Promise<{
    total: number;
    missingCount: number;
    reusableCount: number;
    missing: ManifestEntry[];
  }>;
  now: () => number;
}

const sumSizes = (entries: ManifestEntry[]): number | null =>
  entries.every((e) => typeof e.size === 'number')
    ? entries.reduce((sum, e) => sum + (e.size as number), 0)
    : null;

const bytesMissingFor = (manifest: ManifestEntry[], missing: ManifestEntry[]): number | null => {
  const sizeByName = new Map(manifest.map((e) => [e.file_name, e.size]));
  let sum = 0;
  for (const entry of missing) {
    const size = sizeByName.get(entry.file_name);
    if (typeof size !== 'number') return null;
    sum += size;
  }
  return sum;
};

export const probeDelta = async (
  deps: ProbeDeps,
  manifest: ManifestEntry[],
  version: string,
): Promise<DeltaProbeResult | null> => {
  const startedAt = deps.now();
  try {
    const missing = await deps.getMissingBundleFiles({ manifest, version });
    const manifestBytes = sumSizes(manifest);
    const bytesMissing = manifestBytes === null ? null : bytesMissingFor(manifest, missing.missing);
    // The join can fail even when every manifest entry has a size (a missing entry whose file_name
    // has no match) — that must null OUT bytesTotal too, not just bytesMissing, or the two would
    // diverge and break the "both exact or both null" contract.
    const bytesTotal = bytesMissing === null ? null : manifestBytes;
    return {
      filesTotal: missing.total,
      filesMissing: missing.missingCount,
      probeMs: deps.now() - startedAt,
      bytesTotal,
      bytesMissing,
    };
  } catch {
    return null;
  }
};

export const shouldUseManifest = (
  result: DeltaProbeResult,
  threshold: number = DELTA_MAX_RATIO,
): boolean => result.filesTotal > 0 && result.filesMissing / result.filesTotal < threshold;
